import React, { useState, useRef, useEffect, useMemo } from 'react';
import { sendChatMessage } from '../services/api';
import { parseMarkdown } from '../utils/markdown';
import { resolveYogiAvayogi } from '../utils/yogi';
import { resolveWeekday } from '../utils/weekday';
import PositionsTable from './PositionsTable';
import './ChatInterface.css';

const SUGGESTED_PROMPTS = [
  'What does my current dasha period mean?',
  'What are the strengths in my chart?',
  'What should I know about my career path?',
  'Which remedies are indicated for me?',
];

/**
 * Chat for one profile. History lives in the parent (keyed per profile) and is
 * pushed back up via onMessagesChange, so switching charts never mixes threads.
 */
function ChatInterface({
  birthData,
  chartData,
  messages,
  onMessagesChange,
  profileName,
  onEditDetails,
}) {
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const lastMessageRef = useRef(null);
  const inputRef = useRef(null);
  // True while the chat field has focus. The viewport effect below reads this
  // to leave the layout alone while someone is typing into it.
  const typingRef = useRef(false);
  const settleRef = useRef(null);
  const scrollRef = useRef(null);
  // Collapses the chart pills once the user scrolls, freeing vertical space
  // for the conversation. Only takes visual effect on narrow viewports.
  const [isScrolled, setIsScrolled] = useState(false);
  const [showPositions, setShowPositions] = useState(false);

  const safeMessages = useMemo(
    () => (Array.isArray(messages) ? messages : []),
    [messages]
  );

  // Seed the greeting once per profile, derived from that profile's chart.
  useEffect(() => {
    if (safeMessages.length > 0) return;

    const asc = chartData?.houses?.ascendant?.sign || 'N/A';
    const moon = chartData?.planets?.Moon?.sign || 'N/A';
    const dasha = chartData?.dasha?.current_mahadasha?.lord || 'N/A';
    const yogiInfo = resolveYogiAvayogi(chartData);
    const yogiLine = yogiInfo
      ? `\n\n**Yogi** ${yogiInfo.birthYogi} · **Avayogi** ${yogiInfo.avayogi}`
      : '';

    const birthTithi = chartData?.tithi?.formatted || chartData?.tithi?.name;
    const birthWeekday = (resolveWeekday(chartData) || chartData?.weekday)?.day;
    const panchangaLine =
      birthTithi || birthWeekday
        ? `\n\n**Birth tithi** ${birthTithi || 'N/A'} · **Weekday** ${birthWeekday || 'N/A'}`
        : '';

    onMessagesChange([
      {
        role: 'assistant',
        content: `Namaste. I've cast the chart for **${profileName}** and I'm ready when you are.

**Lagna** ${asc} · **Moon** ${moon} · **Current dasha** ${dasha}${yogiLine}${panchangaLine}

Ask about career, relationships, timing, or anything else you'd like read from this chart.`,
        timestamp: new Date().toISOString(),
      },
    ]);
  }, [safeMessages.length, chartData, profileName, onMessagesChange]);

  // Bring a NEW message into view — but never on first paint, and never
  // because the array identity changed.
  //
  // This used to run on [safeMessages], which is a fresh array on every
  // render, so it also fired while someone was reading. Right after
  // onboarding that meant a swipe up the long welcome message was answered by
  // scrollIntoView({block:'start'}) hauling the view back to the message's
  // top — measured at 150px snapping back to 24px — and 24 is above the 4px
  // restore threshold, so the collapsed pills sprang back too.
  const lastCountRef = useRef(safeMessages.length);
  useEffect(() => {
    const grew = safeMessages.length > lastCountRef.current;
    lastCountRef.current = safeMessages.length;
    if (!grew) return undefined;

    const t = setTimeout(() => {
      // 'nearest' scrolls only when the element is actually out of view, so
      // it never fights a reader who can already see it.
      lastMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
    return () => clearTimeout(t);
  }, [safeMessages.length]);

  // Track the visual viewport so the layout follows the on-screen keyboard.
  //
  // On mobile the keyboard shrinks the visual viewport but not the layout
  // viewport, so a container sized in vh/dvh stays at full height and its
  // input row ends up behind the keyboard. visualViewport reports the space
  // actually visible; --app-vh carries it into the CSS above.
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;

    // The height is frozen while the field has focus.
    //
    // A size threshold was not enough. Mobile browsers hide the URL bar as you
    // type, which grows the visual viewport by roughly 56px — over any
    // sensible threshold — so the container resized and the input moved under
    // the user's finger mid-sentence. Measured at 23px of travel on a 375px
    // viewport.
    //
    // While typing, the keyboard is open and the height that mattered was
    // already measured when it opened. Nothing that happens during typing
    // needs to change it, so nothing does. The pending value is applied on
    // blur, when moving the layout is no longer disruptive.
    let lastHeight = vv.height;
    let pending = null;

    const write = (height) => {
      lastHeight = height;
      document.documentElement.style.setProperty('--app-vh', `${height}px`);
    };

    const apply = () => {
      const height = vv.height;
      if (Math.abs(height - lastHeight) < 24) return;

      // Typing: remember the new height but do not disturb the layout.
      // document.activeElement is the authority here — a ref set from
      // onFocus/onBlur misses focus changes the browser makes itself, and a
      // missed event would quietly re-enable the resizing this prevents.
      const el = inputRef.current;
      const focused = typingRef.current || (el && document.activeElement === el);
      if (focused) {
        pending = height;
        return;
      }

      // Focus has gone but a deferred height is still waiting: take it now.
      // onBlur may not have fired — focus can leave without it — and leaving
      // the layout on a stale height is what this whole effect exists to fix.
      pending = null;

      const shrank = height < lastHeight - 40;
      write(height);

      // Keep the newest message in view as the keyboard animates open.
      if (shrank && scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    };

    // Settle the layout once focus leaves, using whatever we deferred.
    const settle = () => {
      if (pending === null) return;
      pending = null;
      // Use the height as it stands now, not whatever was captured when the
      // change arrived — the keyboard has usually just closed.
      const height = vv.height;
      write(height);
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    };
    settleRef.current = settle;

    write(vv.height);
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      settleRef.current = null;
      document.documentElement.style.removeProperty('--app-vh');
    };
  }, []);

  // Some browsers report the viewport change a beat after focus, so nudge the
  // input into view once the keyboard has settled.
  const handleInputFocus = () => {
    typingRef.current = true;
    setTimeout(() => {
      // Scroll the conversation, not the input. Calling scrollIntoView on a
      // focused field makes some mobile browsers re-seat the caret, which
      // showed up as the cursor moving away mid-sentence.
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }, 300);
  };

  const handleInputBlur = () => {
    typingRef.current = false;
    // Apply any height change that arrived while the field was focused.
    if (settleRef.current) settleRef.current();
  };

  // Escape closes the positions panel.
  useEffect(() => {
    if (!showPositions) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowPositions(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showPositions]);

  // Hysteresis: collapse past 24px, restore only at the very top. A single
  // threshold makes the header flicker when a scroll lands near the boundary.
  //
  // The restore point has to sit at 0, not just below the collapse point.
  // Collapsing the pills shortens the header, which lengthens the scroller and
  // shifts content up by a few pixels; with a 4px restore that shift landed
  // back under the threshold, the pills reopened, the header grew, and the
  // position moved again — a visible flap on every scroll from the top.
  const handleScroll = () => {
    const top = scrollRef.current?.scrollTop ?? 0;
    setIsScrolled((prev) => (prev ? top > 0 : top > 24));
  };

  const submitMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    const userMessage = {
      role: 'user',
      content: trimmed,
      timestamp: new Date().toISOString(),
    };

    const withUser = [...safeMessages, userMessage];
    onMessagesChange(withUser);
    setInputValue('');
    setIsLoading(true);

    try {
      const history = withUser.map((m) => ({ role: m.role, content: m.content }));
      const response = await sendChatMessage(history, chartData, true);

      onMessagesChange([
        ...withUser,
        {
          role: 'assistant',
          content: response.response,
          timestamp: new Date().toISOString(),
          sourcesUsed: response.sources_used,
        },
      ]);
    } catch (error) {
      console.error('Chat error:', error);

      let errorContent =
        'I ran into a problem answering that. Please try again.';

      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorContent =
          'That question is taking longer than expected. Try asking something more specific, or give it another moment.';
      } else if (error.response?.status === 504) {
        errorContent =
          'The request timed out. Try breaking the question into smaller parts.';
      } else if (typeof navigator !== 'undefined' && !navigator.onLine) {
        errorContent =
          'You appear to be offline. Check your connection and try again.';
      }

      onMessagesChange([
        ...withUser,
        {
          role: 'assistant',
          content: errorContent,
          timestamp: new Date().toISOString(),
          isError: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    submitMessage(inputValue);
  };

  const formatTimestamp = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const asc = chartData?.houses?.ascendant?.sign;
  const moon = chartData?.planets?.Moon?.sign;
  const dasha = chartData?.dasha?.current_mahadasha?.lord;
  const yogi = resolveYogiAvayogi(chartData);
  // Tithi is fixed at birth and was always computed correctly, so the stored
  // value is used. The weekday is recomputed: charts cached before the vara
  // fix carry the UTC day rather than the local one.
  const tithi = chartData?.tithi;
  const weekday = resolveWeekday(chartData) || chartData?.weekday;
  const showSuggestions = safeMessages.length <= 1 && !isLoading;

  return (
    <div className="chat-interface">
      <div className="chat-container">
        <div className={`chat-header ${isScrolled ? 'is-condensed' : ''}`}>
          <div className="chart-summary">
            <div className="chart-summary-top">
              <h3>{profileName}</h3>
              <div className="chart-summary-actions">
                <button
                  type="button"
                  className="positions-btn"
                  onClick={() => setShowPositions(true)}
                >
                  Positions
                </button>
                <button type="button" className="edit-details-btn" onClick={onEditDetails}>
                  Edit details
                </button>
              </div>
            </div>
            <div className="chart-badges-wrap" aria-hidden={isScrolled}>
              <div className="chart-badges-inner">
                <div className="chart-badges">
                  {asc && (
                    <span className="badge">
                      <span className="badge-key">Lagna</span>
                      {asc}
                    </span>
                  )}
                  {moon && (
                    <span className="badge">
                      <span className="badge-key">Moon</span>
                      {moon}
                    </span>
                  )}
                  {dasha && (
                    <span className="badge badge-accent">
                      <span className="badge-key">Dasha</span>
                      {dasha}
                    </span>
                  )}
                  {yogi && (
                    <span className="badge badge-accent">
                      <span className="badge-key">Yogi</span>
                      {yogi.birthYogi}
                    </span>
                  )}
                  {yogi && (
                    <span className="badge badge-warn">
                      <span className="badge-key">Avayogi</span>
                      {yogi.avayogi}
                    </span>
                  )}
                  {tithi?.name && (
                    <span className="badge">
                      <span className="badge-key">Tithi</span>
                      {tithi.paksha ? `${tithi.name} (${tithi.paksha})` : tithi.name}
                    </span>
                  )}
                  {weekday?.day && (
                    <span className="badge">
                      <span className="badge-key">Weekday</span>
                      {weekday.day}
                    </span>
                  )}
                  {birthData?.date && (
                    <span className="badge badge-quiet">{birthData.date}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {showPositions && (
          <PositionsTable chartData={chartData} onClose={() => setShowPositions(false)} />
        )}

        <div className="messages-container" ref={scrollRef} onScroll={handleScroll}>
          {safeMessages.map((message, index) => (
            <div
              key={index}
              ref={index === safeMessages.length - 1 ? lastMessageRef : null}
              className={`message ${message.role} ${message.isError ? 'error' : ''}`}
            >
              {message.role === 'assistant' && (
                <span className="message-avatar" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 1.6l2.6 6.9 6.9 2.6-6.9 2.6-2.6 6.9-2.6-6.9L2.5 11.1l6.9-2.6z" />
                  </svg>
                </span>
              )}
              <div className="message-body">
                <div
                  className="message-content"
                  dangerouslySetInnerHTML={{ __html: parseMarkdown(message.content) }}
                />
                <div className="message-meta">
                  <span className="timestamp">{formatTimestamp(message.timestamp)}</span>
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="message assistant loading">
              <span className="message-avatar" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M12 1.6l2.6 6.9 6.9 2.6-6.9 2.6-2.6 6.9-2.6-6.9L2.5 11.1l6.9-2.6z" />
                </svg>
              </span>
              <div className="message-body">
                <div className="typing-indicator">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {showSuggestions && (
          <div className="suggested-prompts">
            {SUGGESTED_PROMPTS.map((prompt) => (
              <button
                key={prompt}
                type="button"
                className="prompt-chip"
                onClick={() => submitMessage(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>
        )}

        <form className="chat-input-form" onSubmit={handleSendMessage}>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder="Ask about your chart…"
            disabled={isLoading}
            className="chat-input"
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isLoading}
            className="send-btn"
            aria-label="Send message"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M8 13V3M3.5 7.5L8 3l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChatInterface;
